import React, { useState, useEffect } from 'react';
import { DollarSign, RefreshCw, Save, Clock, User, Shield, ShieldCheck, ShieldOff, Eye, EyeOff, Lock, Factory } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { settingsAPI } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { useSuperUser } from '../context/SuperUserContext';

export default function SettingsPage() {
  const [exchangeRate, setExchangeRate] = useState(60);
  const [newRate, setNewRate] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Operational Cost
  const [operationalCost, setOperationalCost] = useState(0);
  const [newOperationalCost, setNewOperationalCost] = useState('');
  const [opCostLastUpdate, setOpCostLastUpdate] = useState(null);
  const [savingOpCost, setSavingOpCost] = useState(false);
  
  // Super User
  const { isSuperUser, superUserExpiry, activateSuperUser, deactivateSuperUser } = useSuperUser();
  const [superUserCode, setSuperUserCode] = useState('');
  const [showCode, setShowCode] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Fetch exchange rate
      const rateResponse = await settingsAPI.getExchangeRate();
      setExchangeRate(rateResponse.data.rate);
      setNewRate(rateResponse.data.rate.toString());
      setLastUpdate(rateResponse.data.updated_at);
      setUpdatedBy(rateResponse.data.updated_by);
      
      // Fetch operational cost
      const opCostResponse = await settingsAPI.getOperationalCost();
      setOperationalCost(opCostResponse.data.cost_per_qq);
      setNewOperationalCost(opCostResponse.data.cost_per_qq.toString());
      setOpCostLastUpdate(opCostResponse.data.last_updated);
    } catch (error) {
      toast.error('Error al cargar configuraciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSaveRate = async () => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Ingresa una tasa válida mayor a 0');
      return;
    }

    setSaving(true);
    try {
      const response = await settingsAPI.updateExchangeRate(rate);
      setExchangeRate(response.data.rate);
      setLastUpdate(response.data.updated_at);
      setUpdatedBy(response.data.updated_by);
      toast.success('Tasa de cambio actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOperationalCost = async () => {
    const cost = parseFloat(newOperationalCost);
    if (isNaN(cost) || cost < 0) {
      toast.error('Ingresa un costo válido (puede ser 0)');
      return;
    }

    setSavingOpCost(true);
    try {
      const response = await settingsAPI.updateOperationalCost(cost);
      setOperationalCost(response.data.cost_per_qq);
      setOpCostLastUpdate(response.data.last_updated);
      toast.success('Costo operativo actualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSavingOpCost(false);
    }
  };

  const handleActivateSuperUser = () => {
    if (activateSuperUser(superUserCode)) {
      toast.success('Modo Super Usuario activado por 30 minutos');
      setSuperUserCode('');
    } else {
      toast.error('Código incorrecto');
    }
  };

  const handleDeactivateSuperUser = () => {
    deactivateSuperUser();
    toast.info('Modo Super Usuario desactivado');
  };

  const getRemainingTime = () => {
    if (!superUserExpiry) return '';
    const now = new Date();
    const diff = superUserExpiry - now;
    if (diff <= 0) return 'Expirado';
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Configuración</h1>
        <p className="text-slate-500 mt-1">Ajustes del sistema</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Super User Card */}
        <Card className={`border-2 ${isSuperUser ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200'}`} data-testid="super-user-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isSuperUser ? 'bg-amber-200' : 'bg-slate-100'}`}>
                {isSuperUser ? (
                  <ShieldCheck className="h-6 w-6 text-amber-600" />
                ) : (
                  <Shield className="h-6 w-6 text-slate-500" />
                )}
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  Super Usuario
                  {isSuperUser && (
                    <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                      ACTIVO
                    </span>
                  )}
                </CardTitle>
                <CardDescription>
                  Acceso a funciones de edición, eliminación y registro de stock/costo inicial
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isSuperUser ? (
              <div className="space-y-4">
                <div className="bg-amber-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Modo Super Usuario Activo</p>
                      <p className="text-xs text-amber-600 mt-1">
                        Tiempo restante: {getRemainingTime()}
                      </p>
                    </div>
                    <ShieldCheck className="h-8 w-8 text-amber-500" />
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Tienes acceso a todas las funciones restringidas del sistema incluyendo editar, eliminar 
                  y registrar stock/costo inicial.
                </p>
                <Button 
                  variant="outline" 
                  className="w-full border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={handleDeactivateSuperUser}
                >
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Desactivar Modo Super Usuario
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-4">
                  <p className="text-sm text-slate-600">
                    Ingresa el código maestro para activar el modo Super Usuario. 
                    Este modo permite acceso a funciones restringidas por 30 minutos.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="super-code" className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Código Maestro
                  </Label>
                  <div className="relative">
                    <Input
                      id="super-code"
                      type={showCode ? "text" : "password"}
                      value={superUserCode}
                      onChange={(e) => setSuperUserCode(e.target.value.toUpperCase())}
                      placeholder="Ingresa el código"
                      className="pr-10 font-mono tracking-wider"
                      data-testid="super-user-code-input"
                      onKeyDown={(e) => e.key === 'Enter' && handleActivateSuperUser()}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowCode(!showCode)}
                    >
                      {showCode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button 
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  onClick={handleActivateSuperUser}
                  disabled={!superUserCode}
                  data-testid="activate-super-user-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Activar Super Usuario
                </Button>
              </div>
            )}

            {/* Funciones disponibles */}
            <div className="border-t pt-4 mt-4">
              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Funciones de Super Usuario:</p>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isSuperUser ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                  Editar registros existentes
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isSuperUser ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                  Eliminar registros
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isSuperUser ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                  Registrar stock inicial
                </li>
                <li className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${isSuperUser ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                  Registrar costo inicial
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Exchange Rate Card */}
        <Card className="border-slate-200" data-testid="exchange-rate-card">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle>Tasa de Cambio</CardTitle>
                <CardDescription>Configurar la tasa USD a DOP</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Rate Display */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Tasa Actual</p>
                  <p className="text-3xl font-bold text-[#931A25]">
                    1 USD = {exchangeRate.toFixed(2)} DOP
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={fetchSettings}
                  data-testid="refresh-rate-btn"
                >
                  <RefreshCw className="h-5 w-5" />
                </Button>
              </div>
              {lastUpdate && (
                <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col gap-1 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>Actualizado: {formatDateTime(lastUpdate)}</span>
                  </div>
                  {updatedBy && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>Por: {updatedBy}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Update Rate Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-rate">Nueva Tasa (1 USD = ? DOP)</Label>
                <div className="flex gap-2">
                  <Input
                    id="new-rate"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={newRate}
                    onChange={(e) => setNewRate(e.target.value)}
                    placeholder="Ej: 60.50"
                    className="flex-1"
                    data-testid="exchange-rate-input"
                  />
                  <Button 
                    onClick={handleSaveRate}
                    disabled={saving}
                    className="bg-[#931A25] hover:bg-[#7A151F]"
                    data-testid="save-rate-btn"
                  >
                    {saving ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Guardar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Conversion Preview */}
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-900 mb-2">Vista Previa de Conversión</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-blue-600">USD → DOP</p>
                  <p className="font-medium">$100 USD = RD$ {(100 * parseFloat(newRate || exchangeRate)).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-blue-600">DOP → USD</p>
                  <p className="font-medium">RD$ 1,000 = ${(1000 / parseFloat(newRate || exchangeRate)).toFixed(2)} USD</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operational Cost Card */}
      <Card className="border-slate-200" data-testid="operational-cost-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Factory className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <CardTitle>Costo Operativo por Quintal</CardTitle>
              <CardDescription>
                Este costo se suma al costo de materias primas para obtener el costo total de producción
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Cost Display */}
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700">Costo Operativo Actual</p>
                <p className="text-3xl font-bold text-orange-600">
                  US$ {operationalCost.toFixed(2)} / qq
                </p>
                <p className="text-sm text-orange-500 mt-1">
                  ≈ RD$ {(operationalCost * exchangeRate).toFixed(2)} / qq
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={fetchSettings}
              >
                <RefreshCw className="h-5 w-5" />
              </Button>
            </div>
            {opCostLastUpdate && (
              <div className="mt-3 pt-3 border-t border-orange-200 text-sm text-orange-600">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>Actualizado: {formatDateTime(opCostLastUpdate)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Update Cost Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-op-cost">Nuevo Costo Operativo (USD por quintal)</Label>
              <div className="flex gap-2">
                <Input
                  id="new-op-cost"
                  type="number"
                  step="0.01"
                  min="0"
                  value={newOperationalCost}
                  onChange={(e) => setNewOperationalCost(e.target.value)}
                  placeholder="Ej: 2.50"
                  className="flex-1"
                  data-testid="operational-cost-input"
                />
                <Button 
                  onClick={handleSaveOperationalCost}
                  disabled={savingOpCost}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="save-op-cost-btn"
                >
                  {savingOpCost ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Example Calculation */}
          <div className="bg-slate-100 rounded-lg p-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Ejemplo de Cálculo</p>
            <div className="text-sm text-slate-600 space-y-1">
              <p>Si una fórmula tiene un costo de materias primas de <strong>US$ 15.00/qq</strong></p>
              <p>Y el costo operativo es <strong>US$ {parseFloat(newOperationalCost || operationalCost).toFixed(2)}/qq</strong></p>
              <p className="font-medium text-slate-800 pt-2 border-t">
                Costo Total de Producción = <strong>US$ {(15 + parseFloat(newOperationalCost || operationalCost || 0)).toFixed(2)}/qq</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E97171]/20 flex items-center justify-center flex-shrink-0">
              <DollarSign className="h-5 w-5 text-[#931A25]" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Sobre la Tasa de Cambio</h3>
              <p className="text-sm text-slate-600 mt-1">
                Esta tasa se utiliza para calcular los precios sugeridos de productos en USD 
                y para las conversiones de moneda en el sistema. Actualízala según la tasa 
                del mercado cuando sea necesario.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
